'use client';

import React, { useState } from 'react';
import SocialMediaBar from '@/components/SocialMediaBar';

export default function ContactSection() {
  const [formState, setFormState] = useState({
    name: '',
    email: '',
    company: '',
    message: '',
  });
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormState(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
  };

  return (
    <section id="contact" className="py-28 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="grid lg:grid-cols-2 gap-20 items-start">
          {/* Left: Info */}
          <div className="reveal-hidden-left space-y-10">
            <div>
              <span className="inline-block text-xs font-mono font-semibold text-primary uppercase tracking-[0.3em] mb-4 bg-primary-subtle px-3 py-1.5 rounded-full">
                Get In Touch
              </span>
              <h2
                className="font-heading font-bold text-foreground tracking-tight mb-4"
                style={{ fontSize: 'clamp(2rem, 4vw, 3rem)' }}
              >
                Request a Quote
              </h2>
              <p className="text-foreground-muted text-lg leading-relaxed font-body">
                Tell us about your project and we&apos;ll get back to you within 24 hours with a detailed quotation and proposal.
              </p>
            </div>

            {/* Contact details */}
            <div className="space-y-4">
              {/* Company Info */}
              <div className="flex items-start gap-4 p-5 bg-surface-subtle rounded-2xl border border-border">
                <div className="w-12 h-12 rounded-xl bg-primary-subtle flex items-center justify-center text-primary flex-shrink-0">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
                    <polyline points="9 22 9 12 15 12 15 22"/>
                  </svg>
                </div>
                <div>
                  <div className="text-xs text-foreground-subtle font-mono uppercase tracking-widest mb-1">Company</div>
                  <div className="font-bold text-foreground font-body">FREE-EXPERT LLC</div>
                  <div className="text-sm text-foreground-muted font-body mt-0.5">Registered in Wyoming, USA</div>
                  <div className="text-sm text-foreground-muted font-body">30 N Gould St Ste R, Sheridan, WY 82801</div>
                </div>
              </div>

              <a
                href="mailto:contact@free-expert-llc.com"
                className="flex items-center gap-4 p-5 bg-surface-subtle rounded-2xl border border-border hover:border-primary/30 transition-colors group"
              >
                <div className="w-12 h-12 rounded-xl bg-primary-subtle flex items-center justify-center text-primary flex-shrink-0 group-hover:bg-primary group-hover:text-white transition-colors duration-200">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                    <polyline points="22,6 12,13 2,6"/>
                  </svg>
                </div>
                <div>
                  <div className="text-xs text-foreground-subtle font-mono uppercase tracking-widest mb-0.5">Email</div>
                  <div className="font-semibold text-foreground font-body">contact@free-expert-llc.com</div>
                </div>
              </a>

              <div className="flex items-start gap-4 p-5 bg-surface-subtle rounded-2xl border border-border">
                <div className="w-12 h-12 rounded-xl bg-primary-subtle flex items-center justify-center text-primary flex-shrink-0">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12 19.79 19.79 0 0 1 1.61 3.38 2 2 0 0 1 3.58 1h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.56a16 16 0 0 0 6 6l.92-.92a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/>
                  </svg>
                </div>
                <div>
                  <div className="text-xs text-foreground-subtle font-mono uppercase tracking-widest mb-1">Phone</div>
                  <a href="tel:+13345830869" className="block font-semibold text-foreground font-body hover:text-primary transition-colors">+1 (334) 583-0869</a>
                  <a href="tel:+13345830912" className="block text-sm text-foreground-muted font-body hover:text-primary transition-colors">+1 (334) 583-0912</a>
                </div>
              </div>

              <div className="flex items-center gap-4 p-5 bg-surface-subtle rounded-2xl border border-border">
                <div className="w-12 h-12 rounded-xl bg-primary-subtle flex items-center justify-center text-primary flex-shrink-0">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
                    <circle cx="12" cy="10" r="3"/>
                  </svg>
                </div>
                <div>
                  <div className="text-xs text-foreground-subtle font-mono uppercase tracking-widest mb-0.5">Location</div>
                  <div className="font-semibold text-foreground font-body">Sheridan, Wyoming, USA</div>
                  <div className="text-sm text-foreground-muted font-body">Serving clients worldwide</div>
                </div>
              </div>
            </div>

            {/* Response time badge */}
            <div className="inline-flex items-center gap-2 px-4 py-3 bg-primary-subtle rounded-2xl border border-primary-light">
              <span className="w-2 h-2 rounded-full bg-primary animate-pulse" />
              <span className="text-sm font-semibold text-primary-dark font-body">Average response time: under 24 hours</span>
            </div>

            {/* Social Media */}
            <div className="space-y-3">
              <div className="text-xs font-mono font-semibold text-foreground-subtle uppercase tracking-widest">Follow Free Expert LLC</div>
              <SocialMediaBar variant="horizontal" size="sm" />
            </div>
          </div>

          {/* Right: Form */}
          <div className="reveal-hidden-right">
            {submitted ? (
              <div className="bg-primary-subtle border border-primary-light rounded-3xl p-12 text-center">
                <div className="w-16 h-16 bg-primary rounded-full flex items-center justify-center mx-auto mb-6">
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12"/>
                  </svg>
                </div>
                <h3 className="font-heading font-bold text-2xl text-foreground mb-3">Request Submitted!</h3>
                <p className="text-foreground-muted font-body leading-relaxed">
                  Thank you for your project request. We&apos;ll review your details and send you a detailed quotation within 24 hours.
                </p>
              </div>
            ) : (
              <form
                onSubmit={handleSubmit}
                className="bg-surface-subtle rounded-3xl p-8 border border-border space-y-5"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-sm font-semibold text-foreground mb-2 font-body">
                      Full Name <span className="text-primary">*</span>
                    </label>
                    <input
                      type="text"
                      name="name"
                      required
                      value={formState.name}
                      onChange={handleChange}
                      placeholder="Marcus Johnson"
                      className="form-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-foreground mb-2 font-body">
                      Email Address <span className="text-primary">*</span>
                    </label>
                    <input
                      type="email"
                      name="email"
                      required
                      value={formState.email}
                      onChange={handleChange}
                      placeholder="marcus@company.com"
                      className="form-input"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-foreground mb-2 font-body">
                    Company Name
                  </label>
                  <input
                    type="text"
                    name="company"
                    value={formState.company}
                    onChange={handleChange}
                    placeholder="Your Company Inc."
                    className="form-input"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-foreground mb-2 font-body">
                    Tell us about your project <span className="text-primary">*</span>
                  </label>
                  <textarea
                    name="message"
                    required
                    rows={5}
                    value={formState.message}
                    onChange={handleChange}
                    placeholder="We're looking to build a mobile app for our e-commerce platform..."
                    className="form-input resize-none"
                  />
                </div>

                {/* Service selector */}
                <div>
                  <label className="block text-sm font-semibold text-foreground mb-3 font-body">
                    Service needed
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {['Website', 'Mobile App', 'Social Media', 'Rebranding', 'Other'].map((s) => (
                      <label key={s} className="cursor-pointer">
                        <input type="checkbox" className="sr-only peer" />
                        <span className="inline-block px-4 py-2 rounded-full text-sm font-medium border border-border bg-white text-foreground-muted peer-checked:bg-primary peer-checked:text-white peer-checked:border-primary transition-all duration-200 hover:border-primary/50 font-body">
                          {s}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>

                <button
                  type="submit"
                  className="btn-primary w-full py-4 text-base flex items-center justify-center gap-2"
                >
                  Request a Quote
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="22" y1="2" x2="11" y2="13"/>
                    <polygon points="22 2 15 22 11 13 2 9 22 2"/>
                  </svg>
                </button>

                <p className="text-center text-xs text-foreground-subtle font-body">
                  By submitting, you agree to our{' '}
                  <a href="/privacy-policy" className="text-primary hover:underline">Privacy Policy</a>.
                  No spam, ever.
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}